import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';

const PrivacyPolicy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Theatre booking platform privacy policy - how we collect, use, and protect your personal information."
        keywords={['privacy policy', 'data protection', 'theatre booking', 'personal information']}
        url="/privacy-policy"
      />
      
      <PageHero
        title="Privacy Policy"
        subtitle="How we collect, use and protect your information"
        image="/gallery/image1.jpg"
      />
      
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-xl shadow-md">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="prose prose-lg max-w-none"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Privacy Policy</h2>
              
              <p className="mb-6 text-gray-700">
                Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
              
              <p className="mb-4 text-gray-700">
                Thank you for choosing to use our theatre booking platform. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, and share your personal information when you visit our website, use our services, or communicate with us.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Information We Collect</h3>
              
              <p className="mb-4 text-gray-700">
                We collect information that you provide directly to us when you:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Create an account or profile</li>
                <li>Make a booking or reservation</li>
                <li>Contact our customer service</li>
                <li>Subscribe to our newsletter</li>
                <li>Participate in surveys or promotions</li>
                <li>Post reviews or comments</li>
              </ul>
              
              <p className="mb-4 text-gray-700">
                This information may include your name, email address, phone number, address, payment information, and any other information you choose to provide.
              </p>
              
              <p className="mb-4 text-gray-700">
                We also automatically collect certain information when you visit our website, including:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Log information (IP address, browser type, pages viewed)</li>
                <li>Device information</li>
                <li>Location information</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">How We Use Your Information</h3>
              
              <p className="mb-4 text-gray-700">
                We use the information we collect to:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Process and manage your bookings and transactions</li>
                <li>Provide customer support and respond to your inquiries</li>
                <li>Send you transactional emails and booking confirmations</li>
                <li>Improve our services and develop new features</li>
                <li>Personalize your experience on our platform</li>
                <li>Send promotional communications, if you have opted in</li>
                <li>Detect and prevent fraud or security issues</li>
                <li>Comply with legal obligations</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Information Sharing</h3>
              
              <p className="mb-4 text-gray-700">
                We may share your information with:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Theatres and venues to fulfill your booking</li>
                <li>Payment processors to complete transactions</li>
                <li>Service providers who assist with our business operations</li>
                <li>Legal authorities when required by law</li>
              </ul>
              
              <p className="mb-4 text-gray-700">
                We do not sell your personal information to third parties.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Your Rights and Choices</h3>
              
              <p className="mb-4 text-gray-700">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li>Accessing your personal information</li>
                <li>Correcting inaccurate information</li>
                <li>Deleting your information</li>
                <li>Restricting or objecting to processing</li>
                <li>Data portability</li>
                <li>Withdrawing consent</li>
              </ul>
              
              <p className="mb-4 text-gray-700">
                To exercise these rights, please contact us at <a href="mailto:nampartycelebrations@gmail.com" className="text-indigo-600 hover:text-indigo-500">nampartycelebrations@gmail.com</a>.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Data Security</h3>
              
              <p className="mb-4 text-gray-700">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, accidental loss, or damage. However, no internet transmission is ever completely secure, so we encourage you to take care when disclosing personal information online.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Data Retention</h3>
              
              <p className="mb-4 text-gray-700">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Children's Privacy</h3>
              
              <p className="mb-4 text-gray-700">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us immediately.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Changes to This Policy</h3>
              
              <p className="mb-4 text-gray-700">
                We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website with a revised "Last Updated" date.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Contact Us</h3>
              
              <p className="mb-4 text-gray-700">
                If you have any questions or concerns about our Privacy Policy or data practices, please contact us at:
              </p>
              
              <div className="mb-6 text-gray-700">
                <p>Email: <a href="mailto:nampartycelebrations@gmail.com" className="text-indigo-600 hover:text-indigo-500">nampartycelebrations@gmail.com</a></p>
                <p>Phone: <a href="tel:+918722479114" className="text-indigo-600 hover:text-indigo-500">+91 8722479114</a></p>
                <p>Address: Abhay mansion No 103&104 Devasandra main road Near venugopal swamy temple KR Puram Bangalore 560036</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy; 